import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  App,
  Button,
  Input,
  Modal,
  Select,
  Space,
  Tooltip,
  Typography,
  type TableColumnsType,
  type TableProps,
} from 'antd';
import { BadgePlus, RefreshCw, Search, UserPlus } from 'lucide-react';
import ProTable from '@/components/pro/ProTable';
import { useIamUserList } from '@/pages/iam/composables/useIamUsers';
import { useBatchAddUserToGroup, useIamUserGroupList } from '@/pages/iam/composables/useIamGroups';
import { GrantPermissionDrawer, type GrantPermissionPreset } from '@/pages/iam/components';
import type { ListIAMUserGroupsRequest } from '@/api/iam.api';
import type { IamUserListItem, ListIAMUsersRequest } from '@/types/iam.types';
import { debounce, fmtTime } from '@/utils/utils';
import './users.css';

const PAGE_SIZE = 20;
type UserSortField = 'last_login_at' | 'created_at';
const ALL_GROUPS_REQUEST: ListIAMUserGroupsRequest = {
  page: 1,
  limit: -1,
  name: '',
  display_name: '',
  keyword: '',
};

export default function UserList() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState('');
  const [queryKeyword, setQueryKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [orderBy, setOrderBy] = useState<UserSortField | `-${UserSortField}`>('-last_login_at');
  const [groupingUser, setGroupingUser] = useState<IamUserListItem>();
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [grantDrawer, setGrantDrawer] = useState<{ preset?: GrantPermissionPreset } | null>(null);

  const updateQueryKeyword = useMemo(
    () =>
      debounce((value: string) => {
        setQueryKeyword(value);
        setPage(1);
      }, 300),
    []
  );

  useEffect(() => () => updateQueryKeyword.cancel(), [updateQueryKeyword]);

  const query = useMemo<ListIAMUsersRequest>(
    () => ({
      page,
      limit: pageSize,
      order_by: [orderBy],
      name: '',
      display_name: '',
      keyword: queryKeyword.trim(),
    }),
    [page, pageSize, queryKeyword, orderBy]
  );
  const { data, isLoading, isFetching, error, refetch } = useIamUserList(query);
  const { data: groupsData, isLoading: areGroupsLoading } = useIamUserGroupList(
    ALL_GROUPS_REQUEST,
    !!groupingUser
  );
  const groupMembership = useBatchAddUserToGroup();

  const users = useMemo(() => data?.items ?? [], [data]);
  const groups = useMemo(() => groupsData?.items ?? [], [groupsData]);

  const addUserToGroups = async () => {
    if (!groupingUser || !selectedGroupIds.length) return;
    setSubmitting(true);
    try {
      const targets = groups.filter(
        (group) =>
          selectedGroupIds.includes(group.id) &&
          !group.edges.users?.some((user) => user.id === groupingUser.id)
      );
      await groupMembership.mutateAsync({
        user_id: groupingUser.id,
        add_group_ids: targets.map((group) => group.id),
      });
      message.success(`已将 ${groupingUser.name} 添加至 ${targets.length} 个用户组`);
      setGroupingUser(undefined);
      setSelectedGroupIds([]);
    } finally {
      setSubmitting(false);
    }
  };

  const columns: TableColumnsType<IamUserListItem> = [
    {
      title: '用户',
      key: 'user',
      render: (_, row) => (
        <div className="iam-user-name-cell">
          <Typography.Text strong>{row.name}</Typography.Text>
          <Typography.Text type="secondary">{row.id}</Typography.Text>
        </div>
      ),
    },
    {
      title: '最后登录',
      key: 'last_login_at',
      width: 160,
      sorter: true,
      sortOrder:
        orderBy === 'last_login_at' ? 'ascend' : orderBy === '-last_login_at' ? 'descend' : null,
      sortDirections: ['descend', 'ascend', 'descend'],
      render: (_, r) => (r.last_login_at ? fmtTime(r.last_login_at) : '—'),
    },
    {
      title: '创建时间',
      key: 'created_at',
      width: 160,
      sorter: true,
      sortOrder: orderBy === 'created_at' ? 'ascend' : orderBy === '-created_at' ? 'descend' : null,
      sortDirections: ['descend', 'ascend', 'descend'],
      render: (_, r) => (r.created_at ? fmtTime(r.created_at) : '—'),
    },
    {
      title: '操作',
      width: 136,
      fixed: 'right',
      render: (_, row) => (
        <Space size={0} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="新增授权">
            <Button
              type="text"
              aria-label={`为 ${row.name} 新增授权`}
              icon={<BadgePlus size={15} />}
              onClick={() =>
                setGrantDrawer({ preset: { principalType: 'USER', principalId: row.id } })
              }
            />
          </Tooltip>
          <Tooltip title="添加至用户组">
            <Button
              type="text"
              aria-label={`将 ${row.name} 添加至用户组`}
              icon={<UserPlus size={15} />}
              onClick={() => {
                setGroupingUser(row);
                setSelectedGroupIds([]);
              }}
            />
          </Tooltip>
          {/* 删除用户接口暂不开放，隐藏删除操作。 */}
        </Space>
      ),
    },
  ];

  const handleTableChange: TableProps<IamUserListItem>['onChange'] = (
    _pagination,
    _filters,
    sorter,
    extra
  ) => {
    if (extra.action !== 'sort') return;
    const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const field = activeSorter?.columnKey;
    if (!activeSorter?.order || (field !== 'last_login_at' && field !== 'created_at')) {
      return;
    }
    setOrderBy(activeSorter.order === 'ascend' ? field : `-${field}`);
    setPage(1);
  };

  return (
    <div className="pp-page iam-page">
      <header className="pp-head">
        <h2>用户管理</h2>
        <Space>
          <Button icon={<RefreshCw size={14} />} onClick={() => refetch()}>
            刷新
          </Button>
          {/* 添加用户接口暂不开放，隐藏新建用户操作。 */}
        </Space>
      </header>

      <div className="iam-user-toolbar">
        <Input
          aria-label="搜索关键词"
          className="iam-user-search"
          allowClear
          prefix={<Search size={14} />}
          placeholder="搜索用户名 / 用户 ID"
          value={keyword}
          onChange={(e) => {
            const value = e.target.value;
            setKeyword(value);
            updateQueryKeyword(value);
          }}
        />
      </div>

      <Alert
        className="iam-user-cloud-alert"
        type="info"
        showIcon
        message="用户信息由云平台统一管理"
        description={
          <>
            创建、修改或删除用户，请前往{' '}
            <a
              href="https://cloud.infini-ai.com/iam/identity/user"
              target="_blank"
              rel="noopener noreferrer"
            >
              云平台
            </a>{' '}
            操作。
          </>
        }
      />

      <ProTable<IamUserListItem>
        data={users}
        loading={isLoading}
        tableLoading={isFetching && !isLoading}
        error={error ? (error instanceof Error ? error.message : String(error)) : null}
        columns={columns}
        onChange={handleTableChange}
        rowKey="id"
        onRetry={refetch}
        pagination={{
          current: page,
          pageSize,
          total: data?.totalCount ?? 0,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          showTotal: (count) => `共 ${count} 条`,
          onChange: (nextPage, nextPageSize) => {
            setPage(nextPage);
            setPageSize(nextPageSize);
          },
        }}
        onRow={(row) => ({
          onClick: () => navigate(`/iam/users/${row.id}`),
          style: { cursor: 'pointer' },
        })}
      />

      <Modal
        title={`添加至用户组 · ${groupingUser?.name ?? ''}`}
        open={!!groupingUser}
        okText="确认添加"
        confirmLoading={submitting}
        okButtonProps={{ disabled: !selectedGroupIds.length }}
        onOk={addUserToGroups}
        onCancel={() => {
          setGroupingUser(undefined);
          setSelectedGroupIds([]);
        }}
      >
        <Typography.Paragraph type="secondary">
          可一次选择多个用户组；该用户已经加入的用户组不可重复选择。
        </Typography.Paragraph>
        <Select
          mode="multiple"
          showSearch
          loading={areGroupsLoading}
          optionFilterProp="label"
          style={{ width: '100%' }}
          placeholder="选择一个或多个用户组"
          value={selectedGroupIds}
          onChange={setSelectedGroupIds}
          options={groups.map((group) => ({
            value: group.id,
            label: `${group.display_name || group.name} · ${group.comment || '暂无描述'}`,
            disabled: Boolean(
              groupingUser && group.edges.users?.some((user) => user.id === groupingUser.id)
            ),
          }))}
        />
      </Modal>

      <GrantPermissionDrawer
        open={!!grantDrawer}
        preset={grantDrawer?.preset}
        onClose={() => setGrantDrawer(null)}
        onSuccess={() => {
          void refetch();
        }}
      />
    </div>
  );
}
